import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { X, Send, ExternalLink, RotateCcw } from 'lucide-react';
import { api, inputCls, Button, Field, Notice, StatusBadge } from './ui';

/**
 * Hand the Contact agent one affiliate you already know, with the deal it
 * should open with. The Vetter can check the site first.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const checkCls = 'flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300';

const startForm = (senders) => ({
  email: '',
  name: '',
  domain: '',
  offer_id: '',
  lead_with_offer: true,
  mailbox_id: senders[0] ? String(senders[0].mailbox_id) : '',
  check_first: true,
  note: ''
});

export default function ApproachDialog({ team, onClose, onDone }) {
  const [, setParams] = useSearchParams();
  const senders = team.senders || [];
  const teamDeals = (team.offer_ids || []).join(',');
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState(() => startForm(senders));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // active deals; preselect the first one this team uses
  useEffect(() => {
    let live = true;
    const ids = teamDeals.split(',').filter(Boolean).map(Number);
    api('/offers?status=active')
      .then((d) => {
        if (!live) return;
        const list = d.offers || [];
        const deal = list.find((o) => ids.includes(o.id));
        setOffers(list);
        if (deal) setForm((f) => (f.offer_id ? f : { ...f, offer_id: String(deal.id), lead_with_offer: deal.lead_with_offer ?? true }));
      })
      .catch((err) => { if (live) setError(err.message); });
    return () => { live = false; };
  }, [teamDeals]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const pickDeal = (id) => {
    const deal = offers.find((o) => String(o.id) === id);
    setForm((f) => ({ ...f, offer_id: id, lead_with_offer: deal?.lead_with_offer ?? true }));
  };

  // deals are made by chat on the Deals tab
  const createDeal = () => {
    onClose();
    setParams((p) => { const n = new URLSearchParams(p); n.set('tab', 'deals'); return n; });
  };

  const valid = EMAIL_RE.test(form.email.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const d = await api(`/teams/${team.id}/approach`, {
        method: 'POST',
        body: {
          email: form.email.trim(),
          name: form.name.trim(),
          domain: form.domain.trim(),
          offer_id: form.offer_id ? Number(form.offer_id) : null,
          lead_with_offer: form.lead_with_offer,
          mailbox_id: form.mailbox_id ? Number(form.mailbox_id) : null,
          check_first: form.check_first,
          note: form.note.trim()
        }
      });
      setResult({ id: d.opportunity_id, status: d.status, teamPaused: d.team_paused, workerOff: d.worker_off, checking: form.check_first });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // keep the deal, sender and check choices for the next one
  const again = () => {
    setResult(null);
    setError(null);
    setForm((f) => ({ ...f, email: '', name: '', domain: '', note: '' }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="approach-title"
        className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-xl border border-gray-200 bg-white p-5 shadow-xl dark:border-slate-800 dark:bg-slate-900">
        <div className="mb-4 flex items-start gap-3">
          <div>
            <h3 id="approach-title" className="text-base font-semibold text-gray-900 dark:text-slate-100">Approach an affiliate</h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">The Contact agent writes the first email for {team.name} and handles the follow-ups{team.status === 'active' ? '' : ', with the team still paused: it works this affiliate only, and the Scout stays off'}.</p>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-700 dark:text-slate-500 dark:hover:text-slate-200"><X className="h-5 w-5" /></button>
        </div>

        {result ? (
          <div className="space-y-3">
            {result.workerOff ? (
              <Notice tone="warn">
                Saved, but nothing is written yet: the Affiliate agent team switch is off in User Panel, System control. Switch it on and the first email goes as soon as it is written.
              </Notice>
            ) : (
              <Notice tone="ok">
                The Contact agent is on it{result.checking ? ': the Vetter checks the site, then' : ':'} the first email is written and sent straight away, usually within a minute. It waits for you only if {result.checking ? 'the Vetter has doubts, ' : ''}the checks find a problem in the email, or every email needs your approval.
                {result.teamPaused && ' The team stays paused: it follows up and answers this affiliate, and contacts no one else.'}
              </Notice>
            )}
            {result.status && <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400">Status <StatusBadge status={result.status} /></div>}
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" icon={ExternalLink} onClick={() => onDone(result.id)}>Open affiliate</Button>
              <Button icon={RotateCcw} onClick={again}>Approach another</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <Field label="Email address">
              <input type="email" autoFocus className={inputCls} placeholder="partners@example.com" value={form.email} onChange={(e) => set('email', e.target.value)} />
              {form.email.trim() && !valid && <span className="mt-1 block text-xs text-red-600 dark:text-red-400">That does not look like an email address.</span>}
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name (optional)"><input className={inputCls} value={form.name} onChange={(e) => set('name', e.target.value)} /></Field>
              <Field label="Website (optional)"><input className={inputCls} placeholder="taken from the email domain" value={form.domain} onChange={(e) => set('domain', e.target.value)} /></Field>
            </div>

            <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
              <Field label="Deal" className="min-w-0 flex-1">
                <select className={inputCls} value={form.offer_id} onChange={(e) => pickDeal(e.target.value)}>
                  <option value="">No deal (agent uses the team's deals)</option>
                  {offers.map((o) => <option key={o.id} value={String(o.id)}>{o.name}{o.opening_line ? `: ${o.opening_line}` : ''}</option>)}
                </select>
              </Field>
              <button type="button" onClick={createDeal} className="pb-2 text-sm text-blue-700 hover:underline dark:text-blue-300">Create a deal by chat</button>
            </div>
            <label className={checkCls}>
              <input type="checkbox" checked={form.lead_with_offer} onChange={(e) => set('lead_with_offer', e.target.checked)} /> Mention the deal in the first email
            </label>

            {senders.length > 1 && (
              <Field label="Send from">
                <select className={inputCls} value={form.mailbox_id} onChange={(e) => set('mailbox_id', e.target.value)}>
                  {senders.map((s) => <option key={s.mailbox_id} value={String(s.mailbox_id)}>{s.name ? `${s.name} <${s.email}>` : s.email}</option>)}
                </select>
              </Field>
            )}

            <div>
              <label className={checkCls}>
                <input type="checkbox" checked={form.check_first} onChange={(e) => set('check_first', e.target.checked)} /> Check relevance first
              </label>
              <p className="ml-6 mt-0.5 text-xs text-gray-500 dark:text-slate-400">The Vetter looks at top markets, keywords and the site itself. You still decide if it says no.</p>
            </div>

            <Field label="Anything the agent should know">
              <textarea rows={3} className={inputCls} placeholder="e.g. We met at ICE London, they run the UK casino pages" value={form.note} onChange={(e) => set('note', e.target.value)} />
            </Field>

            {error && <Notice tone="error">{error}</Notice>}
            <div className="flex flex-wrap justify-end gap-2">
              <Button onClick={onClose}>Cancel</Button>
              <Button type="submit" variant="primary" icon={Send} busy={busy} disabled={!valid}>Hand to the Contact agent</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
